import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOverlay } from '../context/OverlayContext';

const LoginPage = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState(''); // New state for confirm password
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [clubName, setClubName] = useState('');
  const [utr, setUtr] = useState('');
  const [wtn, setWtn] = useState('');
  const [address, setAddress] = useState(''); // Honeypot field state, named 'address'
  const [verificationCode, setVerificationCode] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false); // New state for forgot password flow
  const [captchaQuestion, setCaptchaQuestion] = useState(''); // Captcha question state
  const [captchaAnswer, setCaptchaAnswer] = useState('');     // User's captcha answer
  const [captchaSolution, setCaptchaSolution] = useState(0);  // Correct captcha solution
  const navigate = useNavigate();
  const location = useLocation();
  const { showOverlay } = useOverlay();

  // Check if we should show registration form based on navigation state
  React.useEffect(() => {
    if (location.state?.showRegister) {
      setIsRegistering(true);
    }
  }, [location.state]);

  // Generate a new captcha on component mount or when registering state changes
  React.useEffect(() => {
    if (isRegistering) {
      generateCaptcha();
    }
  }, [isRegistering]);

  const generateCaptcha = async () => {
    try {
      const res = await fetch('https://best3intennis.com/best3intennis-api/api/captcha.php', {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        if (data.question) {
          setCaptchaQuestion(data.question);
          setCaptchaAnswer('');
          return;
        }
      }
    } catch (_e) {}
    // Fallback if offline
    const num1 = Math.floor(Math.random() * 9) + 1;
    const num2 = Math.floor(Math.random() * 9) + 1;
    setCaptchaQuestion(`What is ${num1} + ${num2}?`);
    setCaptchaAnswer('');
  };

  const parseJsonResponse = async (response) => {
    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch (_e) {
      const firstBrace = text.indexOf('{');
      const lastBrace = text.lastIndexOf('}');
      if (firstBrace !== -1 && lastBrace > firstBrace) {
        try {
          return JSON.parse(text.substring(firstBrace, lastBrace + 1));
        } catch (_nestedErr) {}
      }
      throw new Error(text.replace(/<[^>]*>?/gm, '').trim() || 'Invalid server response');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('https://best3intennis.com/best3intennis-api/api/login.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }), // Login with email
      });
      if (response.ok) {
        try {
          const data = await parseJsonResponse(response);
          localStorage.setItem('token', data.token); // Store the real token
          if (data.user) {
            localStorage.setItem('user', JSON.stringify({
              id: data.user.id,
              email: data.user.email,
              firstName: data.user.first_name || '',
              lastName: data.user.last_name || ''
            }));
          }
          window.dispatchEvent(new Event('userUpdated'));
          navigate('/dashboard'); // Navigate to the dashboard after login
        } catch (jsonError) {
          console.error('Login error: Error parsing JSON response:', jsonError);
          showOverlay('An unexpected error occurred during login. Please try again.', 'error');
        }
      } else {
        try {
          const data = await parseJsonResponse(response);
          if (data.message === "Account not activated. Please verify your email.") {
            showOverlay(data.message, 'error');
            setIsVerifyingEmail(true); // Redirect to verification flow
          } else {
            showOverlay(data.message || 'Login failed.', 'error');
          }
        } catch (jsonError) {
          console.error('Login error: Error parsing JSON response for non-ok response:', jsonError);
          showOverlay('Invalid email or password.', 'error');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      showOverlay('A network error occurred during login.', 'error');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    // Frontend password validation
    if (password !== confirmPassword) {
      showOverlay('Passwords do not match.', 'error');
      return;
    }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z0-9!@#$%^&*<>?_+\-~]{6,}$/;
    if (!passwordRegex.test(password)) {
      showOverlay('Password must be at least 6 characters long, contain at least one uppercase letter, one lowercase letter, one number, and only allowed special symbols (!@#$%^&*<>?_+-~).', 'error');
      return;
    }

    if (!captchaAnswer || captchaAnswer.trim() === '') {
      showOverlay('Please enter the captcha answer.', 'error');
      return;
    }

    try {
      const response = await fetch('https://best3intennis.com/best3intennis-api/api/register.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          firstName,
          lastName,
          email,
          password,
          clubName,
          utr,
          wtn,
          address,
          captchaAnswer: captchaAnswer.trim(),
          captchaSolution: captchaAnswer.trim()
        }),
      });
      if (response.ok) {
        try {
          const data = await response.json();
          showOverlay('Registration successful! Please check your email for verification.', 'success');
          setIsRegistering(false);
          setIsVerifyingEmail(true); // Move to email verification step
        } catch (jsonError) {
          console.error('Registration error: Error parsing JSON response:', jsonError);
          showOverlay('An unexpected error occurred during registration. Please try again.', 'error');
        }
      } else {
        try {
          const data = await response.json();
          showOverlay(data.message, 'error');
        } catch (jsonError) {
          console.error('Registration error: Error parsing JSON response for non-ok response:', jsonError);
          showOverlay('An unexpected error occurred during registration. Please try again.', 'error');
        }
      }
    } catch (error) {
      console.error('Registration error:', error);
      showOverlay('A network error occurred during registration.', 'error');
    }
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('https://best3intennis.com/best3intennis-api/api/verify_email.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: verificationCode }),
      });
      if (response.ok) {
        showOverlay('Email verified and account activated! You can now log in.', 'success');
        setIsVerifyingEmail(false);
        // Clear registration fields
        setFirstName('');
        setLastName('');
        setEmail('');
        setPassword('');
        setConfirmPassword(''); // Clear confirm password
        setClubName('');
        setUtr('');
        setWtn('');
        setCaptchaAnswer(''); // Clear captcha answer
        setCaptchaQuestion(''); // Clear captcha question
        setCaptchaSolution(0); // Clear captcha solution
        setVerificationCode('');
      } else {
        const data = await response.json();
        showOverlay(data.message, 'error');
      }
    } catch (error) {
      console.error('Email verification error:', error);
      showOverlay('A network error occurred during email verification.', 'error');
    }
  };

  const handleResendVerificationCode = async () => {
    try {
      const response = await fetch('https://best3intennis.com/best3intennis-api/api/resend_verification_code.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      showOverlay(data.message, response.ok ? 'success' : 'error');
    } catch (error) {
      console.error('Resend verification code error:', error);
      showOverlay('A network error occurred while trying to resend the verification code.', 'error');
    }
  };

  const renderLoginForm = () => (
    <form onSubmit={handleLogin} className="space-y-4">
      <div>
        <label htmlFor="loginEmail" className="block text-gray-700 text-sm font-bold mb-2">Email<span className="text-red-500">*</span></label>
        <input
          type="email"
          id="loginEmail"
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-green-500"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>
      <div>
        <label htmlFor="loginPassword" className="block text-gray-700 text-sm font-bold mb-2">Password<span className="text-red-500">*</span></label>
        <input
          type="password"
          id="loginPassword"
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-green-500"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
      </div>
      <button type="submit" className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-6 rounded-md transition duration-300 w-full">
        Login
      </button>
      <button type="button" onClick={() => setIsForgotPassword(true)} className="mt-2 text-sm text-blue-500 hover:text-blue-700 block w-full text-center">
        Forgot Password?
      </button>
    </form>
  );

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('https://best3intennis.com/best3intennis-api/api/forgot_password.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      showOverlay(data.message, response.ok ? 'success' : 'error');
      if (response.ok) {
        setIsForgotPassword(false); // Go back to login form after sending request
        setEmail(''); // Clear email field
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      showOverlay('A network error occurred during the forgot password request.', 'error');
    }
  };

  const renderForgotPasswordForm = () => (
    <form onSubmit={handleForgotPassword} className="space-y-4">
      <div>
        <label htmlFor="forgotEmail" className="block text-gray-700 text-sm font-bold mb-2">Email<span className="text-red-500">*</span></label>
        <input
          type="email"
          id="forgotEmail"
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-green-500"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>
      <button type="submit" className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-6 rounded-md transition duration-300 w-full">
        Send Reset Link
      </button>
    </form>
  );

  const renderRegisterForm = () => (
    <form onSubmit={handleRegister} className="space-y-4">
      {/* Honeypot field - hidden from legitimate users */}
      <div style={{ display: 'none' }}>
        <input
          type="text"
          name="address" // Renamed to 'address'
          value={address}
          onChange={(e) => setAddress(e.target.value)} // Use setAddress
        />
      </div>
      <div>
        <label htmlFor="firstName" className="block text-gray-700 text-sm font-bold mb-2">First Name<span className="text-red-500">*</span></label>
        <input
          type="text"
          id="firstName"
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-green-500"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          required
        />
      </div>
      <div>
        <label htmlFor="lastName" className="block text-gray-700 text-sm font-bold mb-2">Last Name<span className="text-red-500">*</span></label>
        <input
          type="text"
          id="lastName"
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-green-500"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          required
        />
      </div>
      <div>
        <label htmlFor="registerEmail" className="block text-gray-700 text-sm font-bold mb-2">Email<span className="text-red-500">*</span></label>
        <input
          type="email"
          id="registerEmail"
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-green-500"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={handleEmailBlur}
          required
        />
      </div>
      <div>
        <label htmlFor="registerPassword" className="block text-gray-700 text-sm font-bold mb-2">Password<span className="text-red-500">*</span></label>
        <input
          type="password"
          id="registerPassword"
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-green-500"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        {password && !password.match(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{6,}$/) && (
          <p className="text-red-500 text-xs italic mt-1">
            Password must be at least 6 characters long, contain at least one uppercase letter, one lowercase letter, and one number.
          </p>
        )}
      </div>
      <div>
        <label htmlFor="confirmPassword" className="block text-gray-700 text-sm font-bold mb-2">Confirm Password<span className="text-red-500">*</span></label>
        <input
          type="password"
          id="confirmPassword"
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-green-500"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
        />
        {confirmPassword && password !== confirmPassword && (
          <p className="text-red-500 text-xs italic mt-1">Passwords do not match.</p>
        )}
        {confirmPassword && password === confirmPassword && (
          <span className="text-green-500 text-xs italic mt-1">Passwords match!</span>
        )}
      </div>
      <div>
        <label htmlFor="clubName" className="block text-gray-700 text-sm font-bold mb-2">Club Name (Optional)</label>
        <input
          type="text"
          id="clubName"
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-green-500"
          value={clubName}
          onChange={(e) => setClubName(e.target.value)}
        />
      </div>
      <div>
        <label htmlFor="utr" className="block text-gray-700 text-sm font-bold mb-2">UTR (Optional)</label>
        <input
          type="text"
          id="utr"
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-green-500"
          value={utr}
          onChange={(e) => setUtr(e.target.value)}
        />
      </div>
      <div>
        <label htmlFor="wtn" className="block text-gray-700 text-sm font-bold mb-2">WTN (Optional)</label>
        <input
          type="text"
          id="wtn"
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-green-500"
          value={wtn}
          onChange={(e) => setWtn(e.target.value)}
        />
      </div>
      <div>
        <label htmlFor="captchaAnswer" className="block text-gray-700 text-sm font-bold mb-2">
          <i className="fas fa-robot mr-2"></i>Prove you're not a robot: {captchaQuestion}<span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="captchaAnswer"
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-green-500"
          value={captchaAnswer}
          onChange={(e) => setCaptchaAnswer(e.target.value)}
          required
        />
        {captchaAnswer && parseInt(captchaAnswer) !== captchaSolution && (
          <p className="text-red-500 text-xs italic mt-1">Incorrect answer. Please try again.</p>
        )}
      </div>
      <button type="submit" className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-6 rounded-md transition duration-300 w-full">
        Register
      </button>
    </form>
  );

  const handleEmailBlur = async () => {
    if (email) {
      try {
        const response = await fetch('https://best3intennis.com/best3intennis-api/api/check_email.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
        const data = await response.json();
        if (data.exists) {
          showOverlay(
            <div>
              This email is already registered. Please <button type="button" onClick={() => { setIsRegistering(false); setIsForgotPassword(false); }} className="text-blue-500 hover:text-blue-700 underline">log in</button> or <button type="button" onClick={() => { setIsRegistering(false); setIsForgotPassword(true); }} className="text-blue-500 hover:text-blue-700 underline">reset your password</button>.
            </div>, 'error');
        }
      } catch (error) {
        console.error('Email check error:', error);
        showOverlay('Error checking email existence.', 'error');
      }
    }
  };

  const renderVerifyEmailForm = () => (
    <form onSubmit={handleVerifyEmail} className="space-y-4">
      <div>
        <label htmlFor="verifyEmail" className="block text-gray-700 text-sm font-bold mb-2">Email<span className="text-red-500">*</span></label>
        <input
          type="email"
          id="verifyEmail"
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-green-500"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>
      <div>
        <label htmlFor="verificationCode" className="block text-gray-700 text-sm font-bold mb-2">Verification Code<span className="text-red-500">*</span></label>
        <input
          type="text"
          id="verificationCode"
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-green-500"
          value={verificationCode}
          onChange={(e) => setVerificationCode(e.target.value)}
          required
        />
      </div>
      <button type="submit" className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-6 rounded-md transition duration-300 w-full">
        Verify Email
      </button>
      <button type="button" onClick={handleResendVerificationCode} className="mt-2 text-sm text-blue-500 hover:text-blue-700 block w-full text-center">
        Resend Verification Code
      </button>
    </form>
  );

  return (
    <div className="bg-gradient-to-br from-green-100 to-yellow-100 min-h-screen flex items-center justify-center p-4">
      <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-md">
        <h2 className="text-2xl font-bold mb-6 text-center text-green-800">
          {isRegistering ? 'Create Account' : isVerifyingEmail ? 'Verify Email' : isForgotPassword ? 'Forgot Password' : 'Login'}
        </h2>
        {isRegistering ? renderRegisterForm() : isVerifyingEmail ? renderVerifyEmailForm() : isForgotPassword ? renderForgotPasswordForm() : renderLoginForm()}
        <button onClick={() => {
          if (isVerifyingEmail) {
            setIsVerifyingEmail(false);
            setIsRegistering(true);
          } else if (isForgotPassword) {
            setIsForgotPassword(false);
          }
          else {
            setIsRegistering(!isRegistering);
          }
        }} className="mt-4 bg-gray-200 hover:bg-gray-300 text-green-800 font-semibold py-2 px-6 rounded-md transition duration-300 w-full">
          {isRegistering ? 'Already have an account? Login' : isVerifyingEmail ? 'Back to Registration' : isForgotPassword ? 'Back to Login' : "Don't have an account? Register"}
        </button>
      </div>
    </div>
  );
};

export default LoginPage;


